import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryTwoToneIcon from "@mui/icons-material/CategoryTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import ShareIcon from "@mui/icons-material/Share";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TwitterIcon from "@mui/icons-material/Twitter";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { LoadingButton } from "@mui/lab";
import {
	Alert,
	alpha,
	Autocomplete,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { createFormData } from "nhb-toolbox";
import React, { useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import CommonFileUploader from "src/common/CommonFileUploader";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { PriceType } from "../type/offlineExhibitorType";
import ServiceItemRow from "./ServiceItemRow";
import StallItemRow from "./StallItemRow";

const CATEGORY_SUGGESTIONS = [
	"Tourism",
	"Travel Agency",
	"Aviation",
	"Airlines",
	"Tour Operator",
	"Hospitality",
	"Hotel & Resort",
	"Hajj & Umrah",
	"Transport & Rent-a-Car",
	"Eco Tourism",
	"Cruise & Boat",
	"Visa & Immigration",
	"IT & Technology",
	"Government & Association",
];

interface Props {
	open: boolean;
	handleClose: () => void;
}

interface FormValues {
	name: string;
	email: string;
	password: string;
	company_email: string;
	website: string;
	category: string[];
	social_media: {
		facebook: string;
		linkedin: string;
		twitter: string;
		instagram: string;
		youtube: string;
	};
	map: string;
	stalls: {
		hall_name: string;
		stall_no: string;
	}[];
	services: {
		service_name: string;
		price_type: PriceType;
		price: string;
		service_description: string;
	}[];
}

const CreateOfflineExhibitor: React.FC<Props> = ({ open, handleClose }) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [brochureFile, setBrochureFile] = useState<File | null>(null);

	const {
		register,
		control,
		handleSubmit,
		reset,
		setValue,
		getValues,
		watch,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			name: "",
			email: "",
			password: "",
			company_email: "",
			website: "",
			category: [],
			social_media: {
				facebook: "",
				linkedin: "",
				twitter: "",
				instagram: "",
				youtube: "",
			},
			map: "",
			stalls: [{ hall_name: "", stall_no: "" }],
			services: [],
		},
	});

	const {
		fields: stallFields,
		append: appendStall,
		remove: removeStall,
	} = useFieldArray({
		control,
		name: "stalls",
	});

	const prevFirstHallRef = useRef<string>("");

	const handleFirstHallChange = (newHall: string) => {
		const currentStalls = getValues("stalls");
		if (currentStalls && currentStalls.length > 1) {
			currentStalls.forEach((stall, idx) => {
				if (idx > 0) {
					if (!stall.hall_name || stall.hall_name === prevFirstHallRef.current) {
						setValue(`stalls.${idx}.hall_name` as `stalls.${number}.hall_name`, newHall, {
							shouldValidate: true,
						});
					}
				}
			});
		}
		prevFirstHallRef.current = newHall;
	};

	const {
		fields: serviceFields,
		append: appendService,
		remove: removeService,
	} = useFieldArray({
		control,
		name: "services",
	});

	const { mutate: createExhibitor, isPending } = useApiMutation<any, any>({
		method: "POST",
		endPoint: "/api/v1/admin/offline-exhibitor",
		invalidateQueries: ["OfflineExhibitors"],
	});

	const onSubmit = (data: FormValues) => {
		setApiError(null);
		// Filter out empty stalls/services if any
		const cleanedStalls = data.stalls.filter(
			(s) => s.hall_name.trim() !== "" || s.stall_no.trim() !== ""
		);
		const cleanedServices = data.services
			.filter(
				(s) =>
					s.service_name.trim() !== "" ||
					s.price.trim() !== "" ||
					s.service_description.trim() !== ""
			)
			.map((s) => ({
				service_name: s.service_name.trim(),
				price_type: s.price_type || "fixed",
				price: s.price.trim(),
				service_description: s.service_description?.trim() || "",
			}));

		const cleanedCategories = (data.category || []).filter(
			(c) => typeof c === "string" && c.trim() !== ""
		);

		const cleanedSocialMedia: Record<string, string> = {};
		if (data.social_media) {
			Object.entries(data.social_media).forEach(([key, val]) => {
				if (val && typeof val === "string" && val.trim() !== "") {
					cleanedSocialMedia[key] = val.trim();
				}
			});
		}

		const payloadData: Record<string, any> = {
			name: data.name.trim(),
			email: data.email?.trim() || undefined,
			password: data.password?.trim() || undefined,
			company_email: data.company_email?.trim() || undefined,
			website: data.website?.trim() || undefined,
			category: cleanedCategories.length > 0 ? cleanedCategories : undefined,
			social_media:
				Object.keys(cleanedSocialMedia).length > 0
					? cleanedSocialMedia
					: undefined,
			map: data.map?.trim() || undefined,
			stalls: cleanedStalls,
			services: cleanedServices,
		};

		if (logoFile) {
			payloadData.logo = logoFile;
		}
		if (brochureFile) {
			payloadData.brochure = brochureFile;
		}

		const payload = createFormData(payloadData);

		createExhibitor(payload, {
			onSuccess: () => {
				setApiError(null);
				prevFirstHallRef.current = "";
				setLogoFile(null);
				setBrochureFile(null);
				setShowPassword(false);
				reset();
				handleClose();
			},
			onError: (err: any) => {
				const errorMsg =
					err?.response?.data?.message ||
					err?.message ||
					"Failed to create exhibitor. Please check the inputs.";
				setApiError(errorMsg);
			},
		});
	};

	const handleModalClose = () => {
		if (!isPending) {
			setApiError(null);
			prevFirstHallRef.current = "";
			setLogoFile(null);
			setBrochureFile(null);
			setShowPassword(false);
			reset();
			handleClose();
		}
	};

	const currentCategories = watch("category") || [];

	return (
		<Dialog
			open={open}
			onClose={handleModalClose}
			disableEnforceFocus
			maxWidth="md"
			fullWidth
			scroll="paper"
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography variant="h4" fontWeight="bold">
					Create Offline Exhibitor
				</Typography>
				<IconButton onClick={handleModalClose} size="small" disabled={isPending}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<Divider />

			<Box
				component="form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
				sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
			>
				<DialogContent dividers sx={{ py: 2.5 }}>
					<Grid container spacing={2.5}>
						{/* Error Alert Display */}
						{apiError && (
							<Grid item xs={12}>
								<Alert
									severity="error"
									onClose={() => setApiError(null)}
									sx={{ width: "100%", borderRadius: 1.5 }}
								>
									{apiError}
								</Alert>
							</Grid>
						)}

						{/* Section 1: Basic & Account Information */}
						<Grid item xs={12}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
								<BusinessIcon color="primary" fontSize="small" />
								<Typography variant="h5" fontWeight="bold">
									Basic & Account Information
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Exhibitor / Company Name"
								fullWidth
								required
								{...register("name", {
									required: "Exhibitor name is required",
								})}
								error={!!errors.name}
								helperText={errors.name?.message}
								placeholder="e.g. Novoair Limited"
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Account / Login Email"
								fullWidth
								required
								type="email"
								{...register("email", {
									required: "Login email is required",
									pattern: {
										value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
										message: "Invalid email format",
									},
								})}
								error={!!errors.email}
								helperText={errors.email?.message}
								placeholder="e.g. srabon2@yopmail.com"
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Password"
								fullWidth
								required
								type={showPassword ? "text" : "password"}
								{...register("password", {
									required: "Password is required",
									minLength: {
										value: 6,
										message: "Password must be at least 6 characters",
									},
								})}
								error={!!errors.password}
								helperText={errors.password?.message}
								placeholder="Enter password (e.g. 12345678)"
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												onClick={() => setShowPassword((prev) => !prev)}
												edge="end"
												size="small"
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Company Email"
								fullWidth
								type="email"
								{...register("company_email", {
									pattern: {
										value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
										message: "Invalid email format",
									},
								})}
								error={!!errors.company_email}
								helperText={errors.company_email?.message}
								placeholder="e.g. srabon@gmail.com"
							/>
						</Grid>

						<Grid item xs={12}>
							<TextField
								label="Website URL"
								fullWidth
								{...register("website")}
								placeholder="e.g. https://flynovoair.com"
								error={!!errors.website}
								helperText={errors.website?.message}
							/>
						</Grid>

						{/* Section 2: Dedicated Categories Section */}
						<Grid item xs={12}>
							<Paper
								variant="outlined"
								sx={{
									p: 2,
									borderRadius: 2,
									bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
									border: "1px solid",
									borderColor: "divider",
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										flexWrap: "wrap",
										gap: 1,
										mb: 1.5,
									}}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<CategoryTwoToneIcon color="primary" fontSize="small" />
										<Typography variant="h5" fontWeight="bold">
											Exhibitor Categories & Industry Tags
										</Typography>
										<Chip
											label={`${currentCategories.length} selected`}
											size="small"
											color={currentCategories.length > 0 ? "primary" : "default"}
											sx={{ fontWeight: 700, height: 22, fontSize: "0.75rem", borderRadius: 1 }}
										/>
									</Box>
									<Typography variant="caption" color="text.secondary">
										Click popular tags below or type custom & press Enter
									</Typography>
								</Box>

								{/* Quick Preset Suggestion Chips */}
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2 }}>
									{CATEGORY_SUGGESTIONS.map((suggestion) => {
										const isSelected = currentCategories.includes(suggestion);
										return (
											<Chip
												key={suggestion}
												label={suggestion}
												size="small"
												clickable
												color={isSelected ? "primary" : "default"}
												variant={isSelected ? "filled" : "outlined"}
												onClick={() => {
													if (isSelected) {
														setValue(
															"category",
															currentCategories.filter((c) => c !== suggestion),
															{ shouldValidate: true }
														);
													} else {
														setValue("category", [...currentCategories, suggestion], {
															shouldValidate: true,
														});
													}
												}}
												sx={{
													borderRadius: 1.5,
													fontWeight: isSelected ? 700 : 500,
													transition: "all 0.15s ease-in-out",
													"&:hover": {
														transform: "scale(1.03)",
													},
												}}
											/>
										);
									})}
								</Box>

								{/* Autocomplete Multi-select with FreeSolo */}
								<Autocomplete
									multiple
									freeSolo
									options={CATEGORY_SUGGESTIONS}
									value={currentCategories}
									onChange={(_, newValue) => {
										setValue("category", newValue as string[], {
											shouldValidate: true,
										});
									}}
									renderTags={(value: readonly string[], getTagProps) =>
										value.map((option: string, index: number) => (
											<Chip
												variant="filled"
												color="primary"
												size="small"
												label={option}
												{...getTagProps({ index })}
												key={option + index}
												sx={{ borderRadius: 1, fontWeight: 600 }}
											/>
										))
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Add / Search Categories"
											placeholder="Type custom category & press Enter..."
											helperText="Multiple categories supported. Add as many as needed."
										/>
									)}
								/>
							</Paper>
						</Grid>

						{/* Section 4: Stalls Section */}
						<Grid item xs={12}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 1.5,
									mt: 1,
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<StorefrontIcon color="primary" fontSize="small" />
									<Typography variant="h5" fontWeight="bold">
										Stall Information
									</Typography>
								</Box>
								<Button
									type="button"
									size="small"
									variant="outlined"
									startIcon={<AddCircleOutlineIcon />}
									onClick={() =>
										appendStall({
											hall_name: getValues("stalls.0.hall_name") || "",
											stall_no: "",
										})
									}
								>
									Add Stall
								</Button>
							</Box>

							{stallFields.map((field, index) => (
								<StallItemRow
									key={field.id}
									fieldId={field.id}
									index={index}
									control={control as any}
									register={register as any}
									errors={errors as any}
									removeStall={removeStall}
									canDelete={stallFields.length > 1}
									onHallChange={index === 0 ? handleFirstHallChange : undefined}
								/>
							))}
						</Grid>

						{/* Section 5: Services Section */}
						<Grid item xs={12}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 1.5,
									mt: 1,
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<MiscellaneousServicesIcon color="primary" fontSize="small" />
									<Typography variant="h5" fontWeight="bold">
										Services Information
									</Typography>
									<Typography variant="caption" color="text.secondary">
										(Optional)
									</Typography>
								</Box>
								<Button
									type="button"
									size="small"
									variant="outlined"
									startIcon={<AddCircleOutlineIcon />}
									onClick={() =>
										appendService({
											service_name: "",
											price_type: "fixed",
											price: "",
											service_description: "",
										})
									}
								>
									Add Service
								</Button>
							</Box>

							{serviceFields.length === 0 ? (
								<Box
									sx={{
										p: 2,
										textAlign: "center",
										border: "1px dashed",
										borderColor: "divider",
										borderRadius: 1.5,
										color: "text.secondary",
										bgcolor: "action.hover",
									}}
								>
									<Typography variant="body2">
										No services added yet (Optional). Click <b>Add Service</b> if you want to add services.
									</Typography>
								</Box>
							) : (
								serviceFields.map((field, index) => (
									<ServiceItemRow
										key={field.id}
										fieldId={field.id}
										index={index}
										control={control as any}
										register={register as any}
										errors={errors as any}
										removeService={removeService}
										canDelete={true}
									/>
								))
							)}
						</Grid>
						{/* Section 2: Media & Attachments */}
						<Grid item xs={12}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1 }}>
								<AttachFileIcon color="primary" fontSize="small" />
								<Typography variant="h5" fontWeight="bold">
									Logo & Brochure
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} sm={6}>
							<CommonFileUploader
								label="Company Logo"
								accept="image/*"
								selectedFile={logoFile}
								onFileChange={(file) => setLogoFile(file)}
								helperText="Supported formats: JPG, PNG, WEBP, SVG"
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<CommonFileUploader
								label="Company Brochure"
								accept=".pdf,application/pdf,image/*"
								selectedFile={brochureFile}
								onFileChange={(file) => setBrochureFile(file)}
								helperText="Supported formats: PDF or Image"
							/>
						</Grid>

						{/* Section 3: Social Media Links */}
						<Grid item xs={12}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1 }}>
								<ShareIcon color="primary" fontSize="small" />
								<Typography variant="h5" fontWeight="bold">
									Social Media Links
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Facebook URL"
								fullWidth
								size="small"
								placeholder="https://facebook.com/novoair"
								{...register("social_media.facebook")}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<FacebookIcon color="primary" fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="LinkedIn URL"
								fullWidth
								size="small"
								placeholder="https://linkedin.com/company/novoair"
								{...register("social_media.linkedin")}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<LinkedInIcon color="primary" fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Twitter / X URL"
								fullWidth
								size="small"
								placeholder="https://twitter.com/novoair"
								{...register("social_media.twitter")}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<TwitterIcon color="primary" fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Instagram URL"
								fullWidth
								size="small"
								placeholder="https://instagram.com/novoair"
								{...register("social_media.instagram")}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<InstagramIcon color="primary" fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
						</Grid>

						<Grid item xs={12}>
							<TextField
								label="YouTube URL"
								fullWidth
								size="small"
								placeholder="https://youtube.com/@novoair"
								{...register("social_media.youtube")}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<YouTubeIcon color="error" fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
						</Grid>

					</Grid>
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						type="button"
						onClick={handleModalClose}
						variant="outlined"
						disabled={isPending}
						sx={{
							borderColor: "grey.400",
							color: "grey.700",
							"&:hover": { backgroundColor: "grey.100" },
						}}
					>
						Cancel
					</Button>
					<LoadingButton
						type="submit"
						variant="contained"
						color="primary"
						loading={isPending}
					>
						<span>Create Exhibitor</span>
					</LoadingButton>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default CreateOfflineExhibitor;
